import {
  CampaignDetailModelState,
  ContractCompletionType,
  type ContractDetailModel,
  InProgressFormModelFormType,
  DocumentTypes,
  type Form as GeneratedForm,
} from '@urbanx/agx.campaigns.client';
import { DocumentTypesMap, NswContractStage } from '@urbanx/agx-ui-components';
import { getEnumValue } from 'helpers/enumHelper';
import type { useFormLoader } from 'hooks/useFormLoader';
import type { useNewSelectedContract } from 'hooks/useNewSelectedContract';
import { sortMultiOptionButtonOptionsByType } from 'utils/documentUtil';
import FileList from '../Common/FileList';

export const contractSubmissionErrorMessage = 'Failed to start Contract submission';

export const listDetailSeparator = '\u2022';

const qldContractStageLabels: Record<string, string> = {
  ContractDraft: 'Draft',
  BuyerSigning: 'Buyer Signing',
  SignedByBuyer: 'Signed by Buyer',
  VendorSigning: 'Vendor Signing',
  SignedByAllParties: 'Signed by all Parties',
  SubmissionIncomplete: 'Submission Incomplete',
  PendingCompliance: 'Pending Compliance',
  BackUp: 'Back Up',
  Accepted: 'Accepted',
  Unconditional: 'Unconditional',
  Settled: 'Settled',
  Terminated: 'Terminated',
};

const nswContractStageLabels: Record<string, string> = {
  ContractDraft: 'Draft Contract',
  SalesAdviceDraft: 'Sales Advice Draft',
  BuyerSigning: 'Buyer Signing',
  VendorSigning: 'Vendor Signing',
  Signing: 'Signing',
  SubmissionIncomplete: 'Submission Incomplete',
  PendingSolicitorExchange: 'Pending Solicitor Exchange',
  PendingAgentExchange: 'Pending Agent Exchange',
  ContractRequested: 'Contract Requested',
  PendingCompliance: 'Pending Compliance',
  Accepted: 'Accepted',
  Unconditional: 'Unconditional',
  Settled: 'Settled',
  Terminated: 'Terminated',
  Withdrawn: 'Withdrawn',
};

type MultiOptionButtonOption = {
  text: string;
  documentType?: DocumentTypes;
  onClick?: () => void;
};

type ContractDocuments =
  | NonNullable<ReturnType<typeof useNewSelectedContract>>['documents']
  | undefined;

export const withCampaignFormConfig = (form: GeneratedForm) =>
  ({
    ...form,
    formConfigType: 'Campaign',
  }) as unknown as Parameters<ReturnType<typeof useFormLoader>['loadFormWithConfig']>[0];

const inProgressFormDetails: Partial<
  Record<InProgressFormModelFormType, { label: string; title: string }>
> = {
  [InProgressFormModelFormType.ContractExchange]: {
    label: 'Draft Contract',
    title: 'Agent Exchange in progress',
  },
  [InProgressFormModelFormType.ContractRequest]: {
    label: 'Contract',
    title: 'Contract in progress',
  },
  [InProgressFormModelFormType.ContractSubmission]: {
    label: 'Contract Submission',
    title: 'Submission in progress',
  },
};

const nswInProgressFormDetails = {
  ...inProgressFormDetails,
  [InProgressFormModelFormType.ContractRequest]: {
    label: 'Solicitor Exchange',
    title: 'Solicitor Exchange in progress',
  },
};

export const getInProgressContractDetails = (
  state: CampaignDetailModelState,
  contract: ContractDetailModel
) => {
  let formType = contract.inProgressForm?.formType;
  if (
    state === CampaignDetailModelState.NSW &&
    formType === InProgressFormModelFormType.ContractRequest &&
    contract.contractCompletionType === ContractCompletionType.ContractExchange
  ) {
    formType = InProgressFormModelFormType.ContractExchange;
  }

  const formDetails =
    state === CampaignDetailModelState.NSW ? nswInProgressFormDetails : inProgressFormDetails;
  return formType == null ? undefined : formDetails[formType];
};

export const getContractStageLabel = (
  state: CampaignDetailModelState | undefined,
  contract: ContractDetailModel
) => {
  const { contractStage, contractCompletionType, inProgressForm } = contract;
  if (!contractStage) return undefined;

  if (
    state === CampaignDetailModelState.NSW &&
    (getEnumValue(NswContractStage, contractStage) ?? contractStage) ===
      NswContractStage.ContractDraft
  ) {
    const isSolicitorExchange =
      contractCompletionType === ContractCompletionType.SalesAdvice ||
      (contractCompletionType == null &&
        inProgressForm?.formType === InProgressFormModelFormType.ContractRequest);
    return isSolicitorExchange
      ? nswContractStageLabels.SalesAdviceDraft
      : nswContractStageLabels.ContractDraft;
  }

  const stageLabels =
    state === CampaignDetailModelState.QLD ? qldContractStageLabels : nswContractStageLabels;
  return stageLabels[contractStage] ?? contractStage;
};

export const createFileDownloadOptions = (
  documents: ContractDocuments,
  setDownloadFilePath: (filePath: string) => void
) => {
  const options: MultiOptionButtonOption[] = [];

  if (!documents || documents.length === 0) return options;

  documents.forEach((document) => {
    const documentType = DocumentTypes[document.documentType];

    if (documentType === DocumentTypes.SalesContract) return;

    options.push({
      text:
        (documentType
          ? DocumentTypesMap[documentType as keyof typeof DocumentTypesMap]
          : undefined) ??
        documentType ??
        'Document',
      documentType,
      onClick: () => {
        if (!document.containerFilePath) return;

        setDownloadFilePath(document.containerFilePath);
      },
    });
  });

  const textCount = new Map<string, number>();
  const uniqueDocumentList = options.map((item) => {
    const count = textCount.get(item.text) || 0;
    textCount.set(item.text, count + 1);

    return {
      ...item,
      text: count > 0 ? `${item.text}-${count}` : item.text,
    };
  });

  const finalUniqueDocumentList = [
    ...new Map(uniqueDocumentList.map((item) => [item.text, item])).values(),
  ];

  return sortMultiOptionButtonOptionsByType(finalUniqueDocumentList);
};

export const ContractFileList = ({
  documents,
  showDownload,
  state,
  fileDownloadOptions,
}: {
  documents: ContractDocuments;
  showDownload: boolean;
  state: CampaignDetailModelState | undefined;
  fileDownloadOptions: MultiOptionButtonOption[];
}) => {
  if (!documents || documents.length === 0 || !showDownload) return null;

  return (
    <FileList
      id="viewContractSubmissionPdfs"
      files={
        state === CampaignDetailModelState.NSW
          ? fileDownloadOptions.filter((file) => file.documentType !== DocumentTypes.TitleSearch)
          : fileDownloadOptions
      }
    />
  );
};
